import urlConstants from '~/lib/constants/url.constants';

import api from './api';
import { CacheTag } from './CacheTag';
import { ContentTypes, HttpHeaders, HttpRequestMethod } from '..';
import { FilterElement } from '@equip.health/ui';

export interface PatientDocumentsFilters {
  documentTypes?: FilterElement[];
  fileFormats?: FilterElement[];
  uploadedBy?: FilterElement[];
  uploadStartDate?: string;
  uploadEndDate?: string;
}

interface GetAllDocumentsRequest {
  searchTerm?: string;
  patientId: string;
  pageNumber?: number;
  pageSize?: number;
  filters?: PatientDocumentsFilters;
}

interface DeletePatientDocumentRequest {
  documentExternalId: string;
  patientExternalId: string;
  voidReasonCode: string;
}
export interface GetPatientDocumentProvidersResponse {
  externalId: string;
  firstName: string;
  lastName: string;
  displayFirstName?: string;
  displayLastName?: string;
  providerType: string;
  providerAvatar?: AvatarDetails;
}
interface GetPatientDocumentDownloadUrlRequest {
  documentExternalId: string;
  patientId: string;
  fileName: string;
}
export interface GetPatientDocumentDownloadUrlResponse {
  attachmentDownloadUrl: string | null;
  fileValidationStatus: string;
  isValidFile: boolean;
}

interface UploadPatientDocumentResponse {
  httpStatusCode: number;
  data: {
    documentKey: string;
    uploadUrl: string;
  };
  status: string;
}
export const documentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    deletePatientDocument: build.mutation<void, DeletePatientDocumentRequest>({
      invalidatesTags: (_result, error) =>
        error ? [] : [CacheTag.PatientDocuments],
      query: ({ documentExternalId, patientExternalId, voidReasonCode }) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.DELETE,
        url: urlConstants.clinical_data.deletePatientDocument(
          documentExternalId,
          patientExternalId,
          voidReasonCode,
        ),
      }),
    }),
    getAllPatientDocuments: build.infiniteQuery<
      DocumentsObjectV2,
      GetAllDocumentsRequest,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _, lastPageParam) =>
          lastPageParam * 20 < lastPage.count ? lastPageParam + 1 : undefined,
      },
      providesTags: (_result, _error) => [CacheTag.PatientDocuments],

      query: ({ queryArg, pageParam }) => {
        const { patientId, searchTerm, filters } = queryArg;

        return {
          url: urlConstants.clinical_data.getPatientDocumentsV2,
          params: {
            pageNumber: pageParam,
            pageSize: 20,
            patientId,

            ...(searchTerm && { searchTerm }),

            ...(filters?.documentTypes?.length > 0 && {
              documentTypes: filters.documentTypes.map((f) => f.value),
            }),

            ...(filters?.fileFormats?.length > 0 && {
              fileFormats: filters.fileFormats.map((f) => f.value),
            }),

            ...(filters?.uploadedBy?.length > 0 && {
              uploadedByUsers: filters.uploadedBy.map((f) => f.value),
            }),

            ...(filters?.uploadStartDate && {
              fromDate: filters.uploadStartDate,
            }),

            ...(filters?.uploadEndDate && {
              toDate: filters.uploadEndDate,
            }),
          },
        };
      },
    }),
    getPatientDocumentDownloadUrl: build.query<
      GetPatientDocumentDownloadUrlResponse,
      GetPatientDocumentDownloadUrlRequest
    >({
      query: ({ documentExternalId, patientId, fileName }) => ({
        url: urlConstants.clinical_data.getPatientDocumentDownloadURL(
          patientId,
          fileName,
          documentExternalId,
        ),
      }),
    }),
    getPatientDocumentProviders: build.query<
      GetPatientDocumentProvidersResponse[],
      string
    >({
      query: (patientId) => ({
        url: urlConstants.clinical_data.getPatientDocumentProviders,
        params: { patientId },
      }),
    }),
    updateDocument: build.mutation<
      string,
      { documentExternalId: string; body: UpdatePatientDocumentRequest }
    >({
      invalidatesTags: [CacheTag.PatientDocuments],
      query: ({ documentExternalId, body }) => ({
        body,
        method: 'PATCH',
        responseHandler: 'text',
        url: urlConstants.clinical_data.updateDocument(documentExternalId),
      }),
    }),
    uploadPatientDocument: build.mutation<
      UploadPatientDocumentResponse,
      FileMetadata
    >({
      query: (metadata) => ({
        body: metadata,
        method: HttpRequestMethod.POST,
        url: urlConstants.clinical_data.uploadPatientDocument,
      }),
    }),
  }),
});

export const {
  useDeletePatientDocumentMutation,
  useGetAllPatientDocumentsInfiniteQuery,
  useUploadPatientDocumentMutation,
  useUpdateDocumentMutation,
  useGetPatientDocumentProvidersQuery,
  useLazyGetPatientDocumentDownloadUrlQuery,
} = documentsApi;
